// Hero.js
import React, { useState, useEffect, useRef } from 'react';
import './Hero.css';
import banner from '../assets/airmakina_banner.png';
import logo from '../assets/airmakina_logo.png';
import { useNavigate, useLocation } from 'react-router-dom';

/* ===============================
   HERO PARTICLE CANVAS – SICAK TONLARLA
================================ */
const HeroCanvas = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let w, h, raf;
    let gradientOffset = 0;

    const resize = () => {
      w = canvas.width = window.innerWidth;
      h = canvas.height = window.innerHeight > 800 ? 800 : window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const particles = Array.from({ length: 120 }, () => ({
      x: Math.random() * w,
      y: Math.random() * h,
      r: Math.random() * 3 + 0.3,
      vx: (Math.random() - 0.5) * 0.8,
      vy: (Math.random() - 0.5) * 0.8,
      a: Math.random() * 0.5 + 0.2,
      phase: Math.random() * Math.PI * 2
    }));

    const flows = Array.from({ length: 14 }, (_, i) => ({
      y: 50 + i * 55,
      speed: 0.4 + Math.random() * 0.6,
      opacity: 0.15 + Math.random() * 0.35,
      width: Math.random() * 400 + 200,
      x: -500,
      hue: 25 + Math.random() * 20 // ❌ Eski: 180 → ✅ Yeni: 25 (turuncu-kırmızı)
    }));

    const turbines = Array.from({ length: 4 }, (_, i) => ({
      x: w * (0.15 + i * 0.25),
      y: h * 0.65,
      radius: 60 + Math.random() * 70,
      angle: Math.random() * Math.PI * 2,
      speed: 0.007 + Math.random() * 0.015,
      points: 5 + Math.floor(Math.random() * 3)
    }));

    const animate = () => {
      ctx.clearRect(0, 0, w, h);
      gradientOffset += 0.2;

      // Arka plan gradient'ini koyu kırmızı-kahverengi yap
      const bg = ctx.createLinearGradient(0, -gradientOffset % h, 0, h - gradientOffset % h);
      bg.addColorStop(0, 'rgba(22,10,8,0.98)');
      bg.addColorStop(1, 'rgba(15,5,3,0.5)');
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, w, h);

      turbines.forEach(t => {
        t.angle += t.speed;
        ctx.beginPath();
        for (let i = 0; i < t.points * 2; i++) {
          const radius = i % 2 === 0 ? t.radius : t.radius * 0.35;
          const angle = t.angle + (i * Math.PI) / t.points;
          const x = t.x + Math.cos(angle) * radius;
          const y = t.y + Math.sin(angle) * radius;
          if (i === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.closePath();
        const hue = 25 + Math.sin(t.angle * 3) * 10; // ❌ Eski: 190 → ✅ Yeni: 25
        ctx.strokeStyle = `hsla(${hue}, 100%, 60%, 0.2)`;
        ctx.lineWidth = 2;
        ctx.shadowColor = `hsla(${hue}, 100%, 65%, 0.4)`;
        ctx.shadowBlur = 12;
        ctx.stroke();
      });

      flows.forEach(f => {
        f.x += f.speed;
        if (f.x > w + 500) f.x = -500;

        const g = ctx.createLinearGradient(f.x, 0, f.x + f.width, 0);
        g.addColorStop(0, 'rgba(0,0,0,0)');
        g.addColorStop(0.5, `hsla(${f.hue}, 100%, 70%, ${f.opacity})`);
        g.addColorStop(1, 'rgba(0,0,0,0)');

        ctx.beginPath();
        ctx.moveTo(f.x, f.y);
        ctx.lineTo(f.x + f.width, f.y);
        ctx.strokeStyle = g;
        ctx.lineWidth = 2.2;
        ctx.shadowColor = `hsla(${f.hue}, 100%, 70%, 0.5)`;
        ctx.shadowBlur = 7;
        ctx.stroke();
      });

      particles.forEach(p => {
        p.x += p.vx;
        p.y += p.vy;
        p.phase += 0.04;

        if (p.x > w) p.x = 0;
        if (p.x < 0) p.x = w;
        if (p.y > h) p.y = 0;
        if (p.y < 0) p.y = h;

        const hue = 25 + Math.sin(p.phase) * 15; // ❌ Eski: 185 → ✅ Yeni: 25
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `hsla(${hue}, 100%, 75%, ${p.a})`;
        ctx.shadowColor = `hsla(${hue}, 100%, 80%, 0.8)`;
        ctx.shadowBlur = 6;
        ctx.fill();
      });

      raf = requestAnimationFrame(animate);
    };

    animate();
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return <canvas ref={canvasRef} className="hero-particle-canvas" />;
};

/* ===============================
   HERO COMPONENT — DEĞİŞMEDEN KALDI
================================ */
const Hero = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const bannerRef = useRef(null);
  const logoRef = useRef(null);

  const handleTeklifTikla = (e) => {
    e.preventDefault();
    if (location.pathname !== '/') {
      navigate('/', { state: { scrollToTeklif: true } });
    } else {
      window.dispatchEvent(new Event('teklifTikla'));
    }
  };

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleMouseMove = (e) => {
      const x = (e.clientX / window.innerWidth - 0.5) * 40;
      const y = (e.clientY / window.innerHeight - 0.5) * 40;

      if (logoRef.current) {
        logoRef.current.style.transform = `translate(${x * 0.25}px, ${y * 0.25}px)`;
      }
      if (bannerRef.current) {
        bannerRef.current.style.transform = `translate(${x * 0.8}px, ${y * 0.8}px)`;
      }
    };

    const handleScroll = () => {
      const scrollY = window.scrollY;
      if (bannerRef.current) {
        bannerRef.current.style.transform = `translateY(${scrollY * 0.06}px)`;
      }
      if (logoRef.current) {
        logoRef.current.style.transform = `translateY(${scrollY * 0.025}px)`;
      }
    };

    if (bannerRef.current && !isMobile) {
      bannerRef.current.classList.add('glow');
    }

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isMobile]);

  return (
    <section className="hero-container">
      <HeroCanvas />
      <div className="hero-content">
        <div className="hero-text">
          <img src={logo} alt="Air Makina Logo" className="hero-logo" ref={logoRef} />
          <h3>LUPAMAT Satış ve Servis Yetkili Bayisi</h3>
          <p>7/24 Kompresör Tamiri, Bakımı, Satışı ve Yedek Parça Desteği</p>
          <a href="#teklif" onClick={handleTeklifTikla} className="cta-button">
            Teklif Al
          </a>
        </div>

        {!isMobile && (
          <div className="banner-wrapper">
            <img
              src={banner}
              alt="Air Makina Kompresör"
              className="hero-banner-img"
              ref={bannerRef}
            />
          </div>
        )}
      </div>
    </section>
  );
};

export default Hero;